import { ethers } from "ethers";
import {
  curveGaugeVotingAddress,
  fxGaugeVotingAddress,
  gaugeDelegationAddress,
  gaugeVotingABI,
  gaugeDelegationABI,
  convexDaoDelegationAddress,
  convexDaoDelegationABI,
  convexTeamDelegateAddress,
  curveOptOutRegistryAddress,
  fxOptOutRegistryAddress,
  optOutRegistryABI,
  vlCVXAddress,
  vlCVXABI,
  votiumDelegateAddress,
} from "../constants";

const MULTICALL3_ADDRESS = "0xcA11bde05977b3631167028862bE2a173976CA11";
const MULTICALL3_ABI = [
  "function aggregate3(tuple(address target, bool allowFailure, bytes callData)[] calls) view returns (tuple(bool success, bytes returnData)[] returnData)",
];

const ZERO = "0x0000000000000000000000000000000000000000";
const BATCH_LIMIT = 100;

function validatePlatform(platform) {
  if (platform !== "Curve" && platform !== "Fx") {
    throw new Error('Invalid platform. Must be "Curve" or "Fx".');
  }
}

function votingAddress(platform) {
  validatePlatform(platform);
  return platform === "Curve" ? curveGaugeVotingAddress : fxGaugeVotingAddress;
}

function votingContract(platform, providerOrSigner) {
  return new ethers.Contract(
    votingAddress(platform),
    gaugeVotingABI,
    providerOrSigner
  );
}

function delegationContract(providerOrSigner) {
  return new ethers.Contract(
    gaugeDelegationAddress,
    gaugeDelegationABI,
    providerOrSigner
  );
}

function toUnits(value, decimals = 18) {
  return Number(ethers.utils.formatUnits(value, decimals));
}

export function currentEpoch() {
  const now = Math.floor(Date.now() / 1000);
  const epochStart = 1646265600;
  return Math.floor((now - epochStart) / (7 * 24 * 60 * 60));
}

export function currentProposalId() {
  return Math.floor((currentEpoch() - 230) / 2);
}

async function multicall(provider, calls) {
  const multicallContract = new ethers.Contract(
    MULTICALL3_ADDRESS,
    MULTICALL3_ABI,
    provider
  );
  const results = [];
  for (let i = 0; i < calls.length; i += BATCH_LIMIT) {
    const chunk = calls.slice(i, i + BATCH_LIMIT);
    const chunkResults = await multicallContract.callStatic.aggregate3(
      chunk.map((c) => ({
        target: c.target,
        allowFailure: true,
        callData: c.callData,
      }))
    );
    results.push(...chunkResults);
  }
  return results.map((r, i) => {
    if (!r.success || !r.returnData || r.returnData === "0x") {
      return 0;
    }
    try {
      return calls[i].decode(r.returnData);
    } catch {
      return 0;
    }
  });
}

/**
 * Returns active proposal for a platform, or null if none is currently open.
 * Shape: { id, startTime, endTime, epoch, platform }
 */
export async function getActiveProposal(platform, provider) {
  validatePlatform(platform);
  if (!provider) return null;

  const proposalId = currentProposalId();
  const contract = votingContract(platform, provider);
  try {
    const result = await contract.proposals(proposalId);
    const startTime = Number(result.startTime ?? result[0]);
    const endTime = Number(result.endTime ?? result[1]);
    const epoch = Number(result.epoch ?? result[2]);
    const now = Math.floor(Date.now() / 1000);
    if (!startTime || now < startTime || now > endTime) {
      return null;
    }
    return { id: proposalId, startTime, endTime, epoch, platform };
  } catch (e) {
    console.log(`Could not fetch ${platform} proposal`, e);
    return null;
  }
}

/**
 * Multicall gaugeTotal for the given gauges.
 * Returns { [gaugeAddress]: number } in human units.
 */
export async function gaugeTotals(platform, proposalId, gauges, provider) {
  validatePlatform(platform);
  if (!provider || !gauges?.length) return {};

  try {
    const contract = votingContract(platform, provider);
    const validGauges = gauges.filter((g) => {
      try {
        return g && ethers.utils.isAddress(g);
      } catch {
        return false;
      }
    });
    if (!validGauges.length) return {};

    const calls = validGauges.map((gauge) => ({
      target: contract.address,
      callData: contract.interface.encodeFunctionData("gaugeTotal", [
        proposalId,
        ethers.utils.getAddress(gauge),
      ]),
      decode: (data) =>
        toUnits(contract.interface.decodeFunctionResult("gaugeTotal", data)[0]),
    }));

    const totalsRaw = await multicall(provider, calls);
    const totals = {};
    validGauges.forEach((gauge, index) => {
      totals[gauge] = totalsRaw[index];
      totals[gauge.toLowerCase()] = totalsRaw[index];
    });
    return totals;
  } catch (e) {
    console.log("Could not fetch gauge totals", e);
    return {};
  }
}

/**
 * Current-epoch gauge delegate, or null if none / zero address.
 * Use this when the active round's voting auth matters.
 */
export async function getCurrentEpochDelegate(address, provider) {
  if (!address || !provider) return null;
  try {
    const contract = delegationContract(provider);
    const delegate = await contract.getDelegateAtEpoch(address, currentEpoch());
    return !delegate || delegate === ZERO ? null : delegate;
  } catch (e) {
    console.log("Could not fetch current-epoch delegate from GaugeDelegation", e);
    return null;
  }
}

/**
 * Full delegation state for UX: the delegate that is authoritative this epoch
 * (current) and the delegate that takes effect next epoch (future).
 * setDelegate() only affects the next epoch, so these can differ — e.g. a user
 * who just delegated to Votium has future=Votium while current is still unset.
 * Shape: { current, future } where each is an address string or null.
 * On RPC failure returns { error: true } so callers can keep prior state
 * instead of treating the user as undelegated.
 */
export async function getDelegateAddress(address, provider) {
  if (!address || !provider) return { error: true };
  try {
    const contract = delegationContract(provider);
    const epoch = currentEpoch();
    const [currentRaw, futureRaw] = await Promise.all([
      contract.getDelegateAtEpoch(address, epoch),
      contract.getDelegateAtEpoch(address, epoch + 1),
    ]);
    const current = !currentRaw || currentRaw === ZERO ? null : currentRaw;
    const future = !futureRaw || futureRaw === ZERO ? null : futureRaw;
    return { current, future };
  } catch (e) {
    console.log("Could not fetch delegate address from GaugeDelegation", e);
    return { error: true };
  }
}

function isVotium(addr) {
  return (
    !!addr && addr.toLowerCase() === votiumDelegateAddress.toLowerCase()
  );
}

/**
 * Derive UI-relevant flags from the delegation object returned by
 * getDelegateAddress(). Accepts the raw value straight from state:
 *   undefined -> still loading
 *   { error: true } / null -> treat as loading (do not claim "not delegated")
 *   { current, future } -> loaded
 */
export function delegationStatus(delegation) {
  const loading =
    delegation === undefined ||
    delegation === null ||
    delegation?.error === true;
  const current = loading ? null : delegation.current ?? null;
  const future = loading ? null : delegation.future ?? null;
  const isVotiumCurrent = isVotium(current);
  const isVotiumFuture = isVotium(future);
  return {
    loading,
    current,
    future,
    isVotiumCurrent,
    isVotiumFuture,
    // Delegated to Votium for both the active round and going forward.
    isVotiumFull: isVotiumCurrent && isVotiumFuture,
    // Delegated to Votium starting next round, but not for the active round —
    // the user still needs to handle this round's vote manually.
    isVotiumPendingOnly: isVotiumFuture && !isVotiumCurrent,
  };
}

/**
 * Set gauge vote delegate (writes to GaugeDelegation).
 * Pass { wait: false } to return as soon as the tx is submitted (before mined).
 */
export async function setDelegate(provider, delegateAddress, { wait = true } = {}) {
  const signer = provider.getSigner();
  const contract = delegationContract(signer);
  const tx = await contract.setDelegate(delegateAddress);
  if (wait) await tx.wait();
  return tx;
}

/**
 * Set Convex Dao Delegation (used by convexfinance.com gauge weight pages).
 * Pass { wait: false } to return as soon as the tx is submitted (before mined).
 */
export async function setConvexDaoDelegate(
  provider,
  delegateAddress,
  { wait = true } = {}
) {
  const signer = provider.getSigner();
  const contract = new ethers.Contract(
    convexDaoDelegationAddress,
    convexDaoDelegationABI,
    signer
  );
  const tx = await contract.setDelegate(delegateAddress);
  if (wait) await tx.wait();
  return tx;
}

/**
 * Whether the user has already delegated their Convex DAO votes to the Convex
 * team for the upcoming round. The DAO delegation contract shares the gauge
 * delegation ABI but lives at a different address, and we compare against
 * Convex's team delegate (not Votium's).
 */
export async function isDaoDelegatedToConvex(address, provider) {
  if (!address || !provider) return false;
  try {
    const contract = new ethers.Contract(
      convexDaoDelegationAddress,
      gaugeDelegationABI,
      provider
    );
    const delegate = await contract.getDelegateAtEpoch(
      address,
      currentEpoch() + 1
    );
    return (
      !!delegate &&
      delegate !== ZERO &&
      delegate.toLowerCase() === convexTeamDelegateAddress.toLowerCase()
    );
  } catch (e) {
    console.log("Could not fetch Convex DAO delegate", e);
    return false;
  }
}

/**
 * Poll for a transaction receipt on the given provider until it is mined.
 * Uses explicit getTransactionReceipt polling rather than tx.wait(), which can
 * stall for minutes with some injected wallet providers. Resolves with the
 * receipt on success; throws on revert or timeout.
 */
export async function pollForReceipt(
  provider,
  hash,
  { intervalMs = 4000, timeoutMs = 15 * 60 * 1000 } = {}
) {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    let receipt = null;
    try {
      receipt = await provider.getTransactionReceipt(hash);
    } catch {
      // transient RPC hiccup — keep polling
    }
    if (receipt && receipt.blockNumber != null) {
      if (receipt.status === 0) throw new Error("Transaction reverted.");
      return receipt;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error("Timed out waiting for transaction confirmation.");
}

/**
 * Weight-sync status for a delegated user. Compares the user's vlCVX balance at
 * the next epoch against the weight the GaugeDelegation contract has recorded
 * for that epoch. The recorded weight is only accurate to ~1 decimal place, so
 * we compare both in human (18-decimal) units and only flag a sync when the
 * balance exceeds the weight by more than 1 vlCVX. Also reports expired locks
 * (unlockable > 0), which block syncing until the user relocks/processes them.
 */
export async function getWeightSyncStatus(address, provider) {
  if (!address || !provider)
    return { balance: 0, weight: 0, needsSync: false, hasExpiredLocks: false };
  try {
    const epoch = currentEpoch() + 1;
    const vlcvx = new ethers.Contract(vlCVXAddress, vlCVXABI, provider);
    const delegation = delegationContract(provider);
    const [balRaw, weightRaw, lockedBals] = await Promise.all([
      vlcvx.balanceAtEpochOf(epoch, address),
      delegation.userWeightAtEpochOf(epoch, address),
      vlcvx.lockedBalances(address),
    ]);
    const balance = toUnits(balRaw);
    const weight = toUnits(weightRaw);
    const unlockable = lockedBals.unlockable ?? lockedBals[1];
    return {
      balance,
      weight,
      needsSync: balance - weight > 1,
      hasExpiredLocks: !!unlockable && unlockable.gt(0),
    };
  } catch (e) {
    console.log("Could not fetch weight sync status", e);
    return { balance: 0, weight: 0, needsSync: false, hasExpiredLocks: false };
  }
}

/**
 * Sync the user's delegated vote weight in the GaugeDelegation contract.
 * Pass { wait: false } to return as soon as the tx is submitted.
 */
export async function syncDelegation(provider, address, { wait = true } = {}) {
  const contract = delegationContract(provider.getSigner());
  const tx = await contract.sync(address);
  if (wait) await tx.wait();
  return tx;
}

/**
 * On-chain user vote normalized for the UI.
 * Returns null if the user has not voted.
 * Shape: { gauges, weights, voted, vp, baseWeight, adjustedWeight }
 */
export async function getUserVote(platform, proposalId, userAddress, provider) {
  validatePlatform(platform);
  if (!provider || !userAddress) return null;

  try {
    const contract = votingContract(platform, provider);
    const result = await contract.getVote(proposalId, userAddress);
    const gauges = result.gauges ?? result[0];
    const weights = (result.weights ?? result[1]).map((w) =>
      Number(ethers.utils.formatUnits(w, 0))
    );
    const voted = Boolean(result.voted ?? result[2]);
    const baseWeight = toUnits(result.baseWeight ?? result[3]);
    const adjustedRaw = result.adjustedWeight ?? result[4];
    const adjustedWeight = Number(ethers.utils.formatUnits(adjustedRaw, 18));

    if (!voted) return null;

    return {
      gauges,
      weights,
      voted,
      vp: baseWeight,
      baseWeight,
      adjustedWeight,
    };
  } catch (e) {
    console.log("Could not fetch user vote from GaugeVoting", e);
    return null;
  }
}

/**
 * vlCVX balance at a given epoch (human units). Own lock only — does not
 * include gauge weight delegated to this address by others.
 */
export async function balanceAtEpochOf(epoch, userAddress, provider) {
  if (!provider || !userAddress) return 0;
  try {
    const contract = new ethers.Contract(vlCVXAddress, vlCVXABI, provider);
    const bal = await contract.balanceAtEpochOf(epoch, userAddress);
    return toUnits(bal);
  } catch (e) {
    console.log("Could not fetch vlCVX balanceAtEpochOf", e);
    return 0;
  }
}

/**
 * Voting power available to cast a gauge vote at a given epoch (human units).
 * max(own vlCVX lock, GaugeDelegation userWeight) so pure delegate addresses
 * (0 locked CVX, inbound delegated weight only) still get a usable calculator.
 */
export async function votingPowerAtEpochOf(epoch, userAddress, provider) {
  if (!provider || !userAddress) return 0;
  try {
    const vlcvx = new ethers.Contract(vlCVXAddress, vlCVXABI, provider);
    const delegation = delegationContract(provider);
    const [balRaw, weightRaw] = await Promise.all([
      vlcvx.balanceAtEpochOf(epoch, userAddress),
      delegation.userWeightAtEpochOf(epoch, userAddress),
    ]);
    return Math.max(toUnits(balRaw), toUnits(weightRaw));
  } catch (e) {
    console.log("Could not fetch voting power at epoch", e);
    return 0;
  }
}

/**
 * Cast an on-chain gauge vote: vote(voter, gauges, weights).
 * Returns { success: true, hash } or { success: false, error }.
 */
export async function castVote(provider, platform, voter, gauges, weights) {
  validatePlatform(platform);
  try {
    const signer = provider.getSigner();
    const contract = votingContract(platform, signer);
    const tx = await contract.vote(voter, gauges, weights);
    await tx.wait();
    return { success: true, hash: tx.hash };
  } catch (e) {
    const message = e?.error?.message || e?.reason || e?.message || String(e);
    const data = e?.error?.data || e?.data;
    let errorName = null;
    if (data) {
      try {
        errorName = votingContract(platform, provider).interface.parseError(data)
          ?.name;
      } catch {
        /* ignore */
      }
    }
    const code = errorName || message;
    let error = message;
    if (/AlreadyVoted/i.test(code)) error = "You have already voted this round.";
    else if (/NotStarted/i.test(code)) error = "Voting has not started yet.";
    else if (/Ended/i.test(code)) error = "Voting has ended.";
    else if (/MaxWeight/i.test(code))
      error = "Vote weights exceed the maximum (must sum to 1000000).";
    else if (/NoWeight/i.test(code)) error = "No voting weight available.";
    else if (/NotGauge/i.test(code))
      error = "One or more gauges are not valid for this vote.";
    else if (/Mismatch/i.test(code)) error = "Gauge and weight array mismatch.";
    else if (/NotVoteAuth|NotSigner/i.test(code))
      error = "Not authorized to cast this vote.";
    else if (/user rejected|ACTION_REJECTED|denied/i.test(message))
      error = "Transaction rejected.";
    else if (errorName) error = errorName;
    return { success: false, error };
  }
}

function optOutRegistryFor(market) {
  return market === "fxn"
    ? fxOptOutRegistryAddress
    : curveOptOutRegistryAddress;
}

function optOutContract(providerOrSigner, market) {
  return new ethers.Contract(
    optOutRegistryFor(market),
    optOutRegistryABI,
    providerOrSigner
  );
}

function normalizeAddresses(addrs) {
  if (!addrs) return [];
  const seen = new Set();
  const result = [];
  for (const addr of addrs) {
    try {
      const checksummed = ethers.utils.getAddress(addr);
      const key = checksummed.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(checksummed);
    } catch {
      /* skip invalid */
    }
  }
  return result;
}

async function fetchGaugesForUserAtEpoch(contract, user, epoch) {
  try {
    const gauges = await contract.gaugesForUserAtEpoch(user, epoch);
    return normalizeAddresses(gauges);
  } catch {
    try {
      const all = [];
      let startIndex = ethers.BigNumber.from(0);
      const maxScan = 200;
      for (let i = 0; i < 25; i++) {
        const result = await contract.gaugesForUserAtEpochFrom(
          user,
          epoch,
          startIndex,
          maxScan
        );
        const page = result.gauges ?? result[0] ?? [];
        const nextIndex = result.nextIndex ?? result[1];
        const done = Boolean(result.done ?? result[2]);
        all.push(...page);
        if (done) break;
        startIndex = ethers.BigNumber.from(nextIndex);
      }
      return normalizeAddresses(all);
    } catch (e) {
      console.log(`Could not fetch opted-out gauges for epoch ${epoch}`, e);
      return [];
    }
  }
}

function extractRevertData(err) {
  const queue = [err];
  const seen = new Set();
  while (queue.length) {
    const cur = queue.shift();
    if (!cur || seen.has(cur)) continue;
    if (typeof cur === "object") seen.add(cur);
    if (typeof cur === "string") {
      const match = cur.match(/0x[0-9a-fA-F]{8,}/);
      if (match && match[0].length >= 10) return match[0];
      continue;
    }
    if (typeof cur === "object") {
      Object.values(cur).forEach((value) => {
        if (value && (typeof value === "object" || typeof value === "string")) {
          queue.push(value);
        }
      });
    }
  }
  return null;
}

function formatVlCvx(amount) {
  try {
    const value = Number(ethers.utils.formatEther(amount));
    if (!Number.isFinite(value)) return String(amount);
    return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
  } catch {
    return null;
  }
}

function parseOptOutError(e, contract) {
  const message = e?.error?.message || e?.reason || e?.message || String(e);
  const data = extractRevertData(e) || e?.error?.data || e?.data;
  let parsed = null;
  let errorName = e?.errorName || null;
  if (data && contract) {
    try {
      parsed = contract.interface.parseError(data);
      errorName = parsed?.name || errorName;
    } catch {
      /* ignore */
    }
  }
  if (/user rejected|ACTION_REJECTED|denied/i.test(message)) {
    return "Transaction rejected.";
  }
  if (errorName === "AlreadyOptedOut" || /AlreadyOptedOut/i.test(message)) {
    return "Already opted out of one or more selected gauges.";
  }
  if (errorName === "NotOptedOut" || /NotOptedOut/i.test(message)) {
    return "Not currently opted out of one or more selected gauges.";
  }
  if (errorName === "GaugeNotRegistered" || /GaugeNotRegistered/i.test(message)) {
    return "One or more selected gauges are not registered.";
  }
  if (errorName === "InsufficientBalance" || /InsufficientBalance/i.test(message)) {
    const required = parsed?.args?.required ?? parsed?.args?.[1];
    const formatted = required != null ? formatVlCvx(required) : null;
    return formatted
      ? `Need at least ${formatted} vlCVX locked for next epoch to opt out.`
      : "Need more vlCVX locked for next epoch to opt out.";
  }
  if (errorName === "OptOutsArePaused" || /OptOutsArePaused/i.test(message)) {
    return "Opt-outs are currently paused.";
  }
  if (errorName) return errorName;
  if (/execution reverted/i.test(message)) {
    return "Transaction reverted. Check that you have enough vlCVX locked for next epoch.";
  }
  return message;
}

/**
 * Gauges a user is opted out of at the current and next vlCVX epochs.
 * Next is the latest pending state; current still applies during an active vote
 * if the user changed their opt-out after the epoch started.
 */
export async function getUserOptOutGauges(address, provider, market) {
  const epoch = currentEpoch();
  if (!address || !provider) {
    return {
      current: [],
      next: [],
      epoch,
      paused: false,
      canOptOut: false,
      minOptOutBalance: null,
    };
  }
  try {
    const contract = optOutContract(provider, market);
    const locker = new ethers.Contract(vlCVXAddress, vlCVXABI, provider);
    const [current, next, paused, minBal, nextBal] = await Promise.all([
      fetchGaugesForUserAtEpoch(contract, address, epoch),
      fetchGaugesForUserAtEpoch(contract, address, epoch + 1),
      contract.optOutsPaused().catch(() => false),
      contract.minOptOutBalance().catch(() => null),
      locker.balanceAtEpochOf(epoch + 1, address).catch(() =>
        ethers.constants.Zero
      ),
    ]);
    const canOptOut =
      minBal != null && nextBal.gt(0) && nextBal.gte(minBal);
    return {
      current,
      next,
      epoch,
      paused: Boolean(paused),
      canOptOut,
      minOptOutBalance: minBal != null ? formatVlCvx(minBal) : null,
    };
  } catch (e) {
    console.log("Could not fetch user opt-out gauges", e);
    return {
      current: [],
      next: [],
      epoch,
      paused: false,
      canOptOut: false,
      minOptOutBalance: null,
    };
  }
}

/**
 * Gauges a user is opted out of at one vlCVX epoch.
 * That epoch's list is what applies to reward eligibility for the matching vote.
 */
export async function userOptOutGaugesAtEpoch(address, epoch, provider, market) {
  if (!address || !provider || epoch == null) return [];
  try {
    const contract = optOutContract(provider, market);
    return await fetchGaugesForUserAtEpoch(contract, address, epoch);
  } catch (e) {
    console.log("Could not fetch opted-out gauges at epoch", e);
    return [];
  }
}

/**
 * Multicall withheldWeightAtEpoch for the given gauges.
 * Returns { [gaugeAddress]: number } in human units (same scale as gaugeTotals).
 */
export async function withheldWeightsAtEpoch(gauges, epoch, provider, market) {
  if (!provider || !gauges?.length || epoch == null) return {};

  try {
    const contract = optOutContract(provider, market);
    const validGauges = gauges.filter((g) => {
      try {
        return g && ethers.utils.isAddress(g);
      } catch {
        return false;
      }
    });
    if (!validGauges.length) return {};

    const calls = validGauges.map((gauge) => ({
      target: contract.address,
      callData: contract.interface.encodeFunctionData("withheldWeightAtEpoch", [
        ethers.utils.getAddress(gauge),
        epoch,
      ]),
      decode: (data) =>
        toUnits(
          contract.interface.decodeFunctionResult(
            "withheldWeightAtEpoch",
            data
          )[0]
        ),
    }));

    const raw = await multicall(provider, calls);
    const result = {};
    validGauges.forEach((gauge, index) => {
      const value = raw[index] || 0;
      result[gauge] = value;
      result[gauge.toLowerCase()] = value;
    });
    return result;
  } catch (e) {
    console.log("Could not fetch withheld weights", e);
    return {};
  }
}

/**
 * Opt out of rewards on the given gauges. Takes effect next vlCVX epoch.
 */
export async function optOutGauges(provider, gauges, market) {
  const contract = optOutContract(provider.getSigner(), market);
  const addrs = normalizeAddresses(gauges);
  if (!addrs.length) {
    return { success: false, error: "Select at least one gauge." };
  }
  try {
    await contract.callStatic.optOut(addrs);
    const tx = await contract.optOut(addrs);
    await tx.wait();
    return { success: true, hash: tx.hash };
  } catch (e) {
    return { success: false, error: parseOptOutError(e, contract) };
  }
}

/**
 * Opt back in to rewards on the given gauges. Takes effect next vlCVX epoch.
 */
export async function optInGauges(provider, gauges, market) {
  const contract = optOutContract(provider.getSigner(), market);
  const addrs = normalizeAddresses(gauges);
  if (!addrs.length) {
    return { success: false, error: "Select at least one gauge." };
  }
  try {
    const tx = await contract.optIn(addrs);
    await tx.wait();
    return { success: true, hash: tx.hash };
  } catch (e) {
    return { success: false, error: parseOptOutError(e, contract) };
  }
}
